// Firebase client singleton — SIGNAL control plane
import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as fbSignOut,
  onAuthStateChanged,
  updatePassword as fbUpdatePassword,
  updateEmail as fbUpdateEmail,
  sendEmailVerification as fbSendEmailVerification,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';

const REQUIRED_VARS = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_APP_ID',
];

export function validateFirebaseConfig() {
  const missing = REQUIRED_VARS.filter((k) => !process.env[k]);
  return { ok: missing.length === 0, missing };
}

export function createFirebaseConfigError(missing) {
  const err = new Error(`Firebase configuration incomplete. Missing env vars: ${missing.join(', ')}. Set them in your deployment (e.g. Vercel) and redeploy.`);
  err.code = 'signal/firebase-config-missing';
  err.missing = missing;
  return err;
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export function getFirebaseApp() {
  const v = validateFirebaseConfig();
  if (!v.ok) {
    throw createFirebaseConfigError(v.missing);
  }
  return getApps()[0] || initializeApp(firebaseConfig);
}

export function getFirebaseAuth() {
  const app = getFirebaseApp();
  const auth = getAuth(app);
  setPersistence(auth, browserLocalPersistence).catch(() => {});
  return auth;
}

export async function signIn(email, password) {
  const auth = getFirebaseAuth();
  return signInWithEmailAndPassword(auth, email, password);
}

export async function signInWithGoogle() {
  const auth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return signInWithPopup(auth, provider);
}

export async function signOut() {
  const auth = getFirebaseAuth();
  return fbSignOut(auth);
}

export function watchAuth(callback) {
  const auth = getFirebaseAuth();
  return onAuthStateChanged(auth, callback);
}

export async function updatePassword(currentPassword, newPassword) {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');
  const cred = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, cred);
  return fbUpdatePassword(user, newPassword);
}

export async function updateEmail(currentPassword, newEmail) {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');
  const cred = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, cred);
  return fbUpdateEmail(user, newEmail);
}

export async function sendVerificationEmail() {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');
  return fbSendEmailVerification(user);
}

export async function getIdToken(forceRefresh = false) {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

export async function getIdTokenResult(forceRefresh = false) {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdTokenResult(forceRefresh);
}
